import type { UserRole } from '@/types/auth';
import { type useCreatorFilters } from './use-creator-filters';
import { alertedClause } from './creators-alert-clauses';

type FilterState = ReturnType<typeof useCreatorFilters.getState>;

function roleFilter(
  role: UserRole | undefined,
  id: string | undefined
): string {
  if (!role || !id || role === 'admin') return '';
  if (role === 'approacher') return `assigned_approacher = "${id}"`;
  if (role === 'mago') return `assigned_mago = "${id}"`;
  return `collector = "${id}"`;
}

function collectorFilter(value: string): string {
  const s = value.replace(/"/g, '\\"');
  return `(collector.first_name ~ "${s}" || collector.last_name ~ "${s}")`;
}

function searchFilter(value: string): string {
  const s = value.replace(/"/g, '\\"');
  return `(ig_username ~ "${s}" || display_name ~ "${s}" || email ~ "${s}" || bio ~ "${s}")`;
}

function fieldFilters(state: FilterState): string[] {
  const parts: string[] = [];
  if (state.category) parts.push(`category = ${state.category}`);
  if (state.creator_type) parts.push(`creator_type = "${state.creator_type}"`);
  if (state.rank) parts.push(`rank = ${state.rank}`);
  if (state.collector) parts.push(collectorFilter(state.collector));
  if (state.status) parts.push(`status = "${state.status}"`);
  if (state.sex) parts.push(`sex = "${state.sex}"`);
  if (state.search) parts.push(searchFilter(state.search));
  const ac = alertedClause(state.alertedFilter);
  if (ac) parts.push(ac);
  return parts;
}

// Collectors don't see leads they themselves marked as "discarded" via the
// "Eliminar lead" button (user_deleted_at). That button's intent is the
// collector's personal trash; keeping those leads in their active list
// contradicted the button's promise. Admins still see them (with the
// "Descartado" badge in the table) so nothing is lost from the audit
// trail. Trash view (`deleted_at != ""`) is admin's papelera and is
// orthogonal — user_deleted_at isn't filtered there.
function hideUserDeletedClause(
  role: UserRole | undefined,
  view: FilterState['view']
): string {
  if (view === 'trash') return '';
  if (role === 'collector') return 'user_deleted_at = ""';
  return '';
}

// Cada pestaña es una partición sin solape de la tabla: los alertados
// (manual o IA, `alert_pending_at` unifica ambos desde la migración 176)
// salen de "Activos" y viven en la suya, así que la lista de activos solo
// muestra leads sanos. La papelera es ortogonal — ahí no se separa por
// alerta.
const VIEW_FILTER: Record<FilterState['view'], string> = {
  active: 'deleted_at = "" && alert_pending_at = ""',
  alerted: 'deleted_at = "" && alert_pending_at != ""',
  trash: 'deleted_at != ""',
};

export function buildBaseFilter(
  state: FilterState,
  role: UserRole | undefined,
  userId?: string
): string {
  const viewPart = VIEW_FILTER[state.view];
  const rf = roleFilter(role, userId);
  const hide = hideUserDeletedClause(role, state.view);
  const parts = [viewPart];
  if (rf) parts.push(rf);
  if (hide) parts.push(hide);
  parts.push(...fieldFilters(state));
  return parts.join(' && ');
}

// Sort simple: el del usuario y nada más. Antes "Activos" llevaba un
// -alert_pending_at delante para que los alertados burbujearan al inicio;
// con la pestaña "Alertados" propia ya no hay nada que burbujear y el
// orden que pide el usuario se respeta tal cual.
//
// Cada pestaña reinterpreta el sort por defecto ('created') hacia la fecha
// que la define: deleted_at en Papelera, alert_pending_at en Alertados.
const DEFAULT_SORT_KEY: Partial<Record<FilterState['view'], string>> = {
  alerted: 'alert_pending_at',
  trash: 'deleted_at',
};

export function buildSort(filters: FilterState): string {
  const fallback = DEFAULT_SORT_KEY[filters.view];
  const sortKey =
    fallback && filters.sortKey === 'created' ? fallback : filters.sortKey;
  return `${filters.sortDir === 'desc' ? '-' : ''}${sortKey}`;
}

export type FiltersBundle = {
  filter: string;
  sort: string;
  expand: string;
};

type BundleArgs = {
  filters: FilterState;
  role: UserRole | undefined;
  userId: string | undefined;
};

export function buildFiltersBundle(a: BundleArgs): FiltersBundle {
  const filter = buildBaseFilter(a.filters, a.role, a.userId);
  const sort = buildSort(a.filters);
  const expand =
    a.filters.view === 'trash' ? 'collector,deleted_by' : 'collector';
  return { filter, sort, expand };
}
